/**
 * Affiliate Portal (E-013 / F-035)
 * 
 * STANDALONE public portal for external affiliate partners.
 * Affiliates do NOT need Cognito / main system login.
 * They use email + affiliate code to access their dashboard.
 * 
 * Features:
 * - Self-registration form
 * - Login with email + affiliate code
 * - Real-time referral performance metrics
 * - One-click referral link copy (US-034)
 * - Commission payout status (US-035: Trial / Pending Payout / Paid)
 * - Marketing resources and swipe copy (T-007)
 */

import React, { useState, useEffect } from 'react';

const API = process.env.REACT_APP_API_URL || '';

// ============================================
// TYPES
// ============================================
interface AffiliateMetrics {
    totalClicks: number;
    totalTrials: number;
    totalConversions: number;
    conversionRate: string;
    totalEarnings: number;
    pendingPayout: number;
    paidOut: number;
}

interface AffiliateInfo {
    id: string;
    name?: string;
    email: string;
    company?: string;
    companyName?: string;
    type?: string;
    code: string;
    status: 'pending' | 'approved' | 'suspended';
    strikesCount?: number;
    commissionRate?: number;
    cookieWindowDays: number;
    twoFactorEnabled?: boolean;
    agreementAcceptedAt?: string;
    trials?: number;
    conversions?: number;
    earnings?: number;
}

interface Referral {
    id: string;
    referredEmail: string;
    status: string;
    commissionRate: string;
    commissionAmount: number;
    paymentMonth: number;
    createdAt: string;
}

interface MarketingResource {
    id: string;
    category: string;
    title: string;
    description: string;
    format: string;
    content?: string;
    downloadUrl?: string;
    tags: string[];
}

// ============================================
// REGISTRATION FORM (with Partnership Agreement)
// ============================================
interface AgreementSection {
    heading: string;
    content: string;
}

interface AgreementData {
    version: string;
    effectiveDate: string;
    title: string;
    sections: AgreementSection[];
}

function RegistrationForm({ onRegistered }: { onRegistered: (code: string, email: string) => void }) {
    const [form, setForm] = useState({ name: '', email: '', company: '', type: 'influencer', website: '' });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [agreement, setAgreement] = useState<AgreementData | null>(null);
    const [agreedToTerms, setAgreedToTerms] = useState(false);
    const [showAgreement, setShowAgreement] = useState(false);
    const [agreementRead, setAgreementRead] = useState(false);

    useEffect(() => {
        fetch(`${API}/api/affiliates/agreement`)
            .then((res: any) => res.json())
            .then((data: any) => {
                if (data.success) setAgreement(data.data);
            })
            .catch(() => { });
    }, []);

    const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
        const el = e.currentTarget;
        if (el.scrollTop + el.clientHeight >= el.scrollHeight - 30) {
            setAgreementRead(true);
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!agreedToTerms) {
            setError('You must read and accept the Partnership Agreement to proceed.');
            return;
        }
        setLoading(true);
        setError('');
        try {
            const res = await fetch(`${API}/api/affiliates/register`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ ...form, agreedToTerms: true })
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.message || 'Registration failed');

            setSuccess(`Registration successful! Your affiliate code is: ${data.data.code}. Save this — it's your login credential.`);
            onRegistered(data.data.code, form.email);
        } catch (err: any) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="max-w-2xl mx-auto bg-white/95 backdrop-blur-2xl p-8 md:p-14 rounded-[2.5rem] shadow-2xl border border-white/40">
            <h2 className="text-4xl font-black text-gray-900 mb-2 tracking-tighter">
                Elite Partner Network
            </h2>
            <p className="text-gray-500 mb-10 text-lg font-medium">
                Join our ecosystem and earn <span className="text-primary font-black">20% recurring</span> commissions.
            </p>

            {error && (
                <div className="bg-zinc-50 border border-zinc-100 rounded-2xl p-4 text-zinc-600 text-sm font-bold flex items-center gap-3 mb-8">
                    <i className="fas fa-exclamation-circle text-zinc-400"></i> {error}
                </div>
            )}
            {success && (
                <div className="bg-success/10 border border-success/20 rounded-2xl p-4 text-success text-sm font-bold flex items-center gap-3 mb-8">
                    <i className="fas fa-check-circle"></i> {success}
                </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                        <label className="text-[10px] font-black uppercase tracking-widest text-gray-400 ml-1">Full Name *</label>
                        <input required className="w-full bg-slate-50 border border-slate-100 rounded-2xl px-6 py-4 text-gray-900 font-semibold outline-none focus:border-primary/30 transition-all" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="John Smith" />
                    </div>
                    <div className="space-y-2">
                        <label className="text-[10px] font-black uppercase tracking-widest text-gray-400 ml-1">Email Address *</label>
                        <input required type="email" className="w-full bg-slate-50 border border-slate-100 rounded-2xl px-6 py-4 text-gray-900 font-semibold outline-none focus:border-primary/30 transition-all" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} placeholder="john@example.com" />
                    </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                        <label className="text-[10px] font-black uppercase tracking-widest text-gray-400 ml-1">Company / Brand</label>
                        <input className="w-full bg-slate-50 border border-slate-100 rounded-2xl px-6 py-4 text-gray-900 font-semibold outline-none focus:border-primary/30 transition-all" value={form.company} onChange={e => setForm({ ...form, company: e.target.value })} placeholder="Optional" />
                    </div>
                    <div className="space-y-2">
                        <label className="text-[10px] font-black uppercase tracking-widest text-gray-400 ml-1">Partner Type</label>
                        <select className="w-full bg-slate-50 border border-slate-100 rounded-2xl px-6 py-4 text-gray-900 font-semibold outline-none focus:border-primary/30 transition-all appearance-none" value={form.type} onChange={e => setForm({ ...form, type: e.target.value })}>
                            <option value="influencer">Content Creator / Influencer</option>
                            <option value="tax_advisor">Tax Advisor / Accountant</option>
                            <option value="agent">Letting Agent</option>
                            <option value="partner">Technology Partner</option>
                        </select>
                    </div>
                </div>

                <div className="space-y-2">
                    <label className="text-[10px] font-black uppercase tracking-widest text-gray-400 ml-1">Website / Social Link</label>
                    <input className="w-full bg-slate-50 border border-slate-100 rounded-2xl px-6 py-4 text-gray-900 font-semibold outline-none focus:border-primary/30 transition-all" value={form.website} onChange={e => setForm({ ...form, website: e.target.value })} placeholder="https://..." />
                </div>

                {/* Partnership Agreement Section */}
                <div className="mt-10 border border-slate-100 rounded-3xl overflow-hidden bg-slate-50/50">
                    <button
                        type="button"
                        onClick={() => setShowAgreement(!showAgreement)}
                        className="w-full p-6 flex justify-between items-center group transition-colors hover:bg-white"
                    >
                        <span className="font-black text-gray-900 text-sm tracking-tight flex items-center gap-3">
                            <i className="fas fa-file-contract text-primary"></i> Affiliate Partnership Agreement {agreement ? `(v${agreement.version})` : ''}
                        </span>
                        <span className="text-primary font-black text-[10px] uppercase tracking-widest bg-primary/5 px-3 py-1 rounded-full group-hover:bg-primary group-hover:text-white transition-all">
                            {showAgreement ? 'Hide' : 'Read'}
                        </span>
                    </button>

                    {showAgreement && agreement && (
                        <div
                            onScroll={handleScroll}
                            className="max-h-[300px] overflow-y-auto px-8 pb-8 bg-white no-scrollbar"
                        >
                            <div className="py-4 border-b border-slate-50 mb-6 sticky top-0 bg-white">
                                <h3 className="text-xl font-black text-gray-900 leading-none mb-1">
                                    {agreement.title}
                                </h3>
                                <p className="text-[10px] font-bold text-gray-300 uppercase tracking-widest">
                                    Effective: {agreement.effectiveDate} · Rev {agreement.version}
                                </p>
                            </div>

                            <div className="space-y-8">
                                {agreement.sections.map((section, i) => (
                                    <div key={i}>
                                        <h4 className="text-xs font-black text-gray-900 uppercase tracking-widest mb-3 flex items-center gap-2">
                                            <span className="w-5 h-5 bg-slate-900 text-white rounded-full flex items-center justify-center text-[10px]">{i + 1}</span>
                                            {section.heading}
                                        </h4>
                                        <p className="text-sm text-gray-500 leading-relaxed font-medium">
                                            {section.content}
                                        </p>
                                    </div>
                                ))}
                            </div>

                            {!agreementRead && (
                                <div className="text-center py-6 text-primary animate-bounce text-[10px] font-black uppercase tracking-widest">
                                    Scroll to accept
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* Agreement Checkbox */}
                <div className="pt-4 flex items-start gap-4 p-4 rounded-2xl bg-primary/[0.02] border border-primary/5">
                    <div className="relative flex items-center">
                        <input
                            type="checkbox"
                            className="w-6 h-6 border-2 border-slate-200 rounded-lg appearance-none checked:bg-primary checked:border-primary transition-all cursor-pointer"
                            id="agree-terms"
                            checked={agreedToTerms}
                            onChange={(e) => {
                                if (!showAgreement) {
                                    setShowAgreement(true);
                                    setError('Please read the agreement details first.');
                                    return;
                                }
                                setAgreedToTerms(e.target.checked);
                                if (e.target.checked) setError('');
                            }}
                        />
                        {agreedToTerms && <i className="fas fa-check absolute left-1.5 text-white text-[10px] pointer-events-none"></i>}
                    </div>
                    <label htmlFor="agree-terms" className="text-xs text-gray-500 leading-relaxed font-semibold cursor-pointer">
                        I hereby accept the <strong className="text-gray-900">Affiliate Partnership Agreement</strong>. I understand the performance standards, minimum activity requirements, and termination clauses defined in Version {agreement?.version || '1.0'}.
                    </label>
                </div>

                <button
                    type="submit"
                    disabled={loading || !agreedToTerms}
                    className={`w-full py-5 rounded-2xl font-black text-white text-lg tracking-tight transition-all shadow-xl ${
                        (loading || !agreedToTerms) 
                        ? 'bg-slate-200 cursor-not-allowed grayscale' 
                        : 'bg-gradient-to-r from-primary to-secondary hover:scale-[1.02] active:scale-[0.98] shadow-primary/25'
                    }`}
                >
                    {loading ? 'Processing Application...' : 'Submit Application'}
                </button>
            </form>
        </div>
    );
}

// ============================================
// LOGIN FORM (email + affiliate code)
// ============================================
function AffiliateLogin({ onLogin }: { onLogin: (token: string) => void }) {
    const [email, setEmail] = useState('');
    const [code, setCode] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [mfaRequired, setMfaRequired] = useState(false);
    const [challengeToken, setChallengeToken] = useState('');
    const [mfaCode, setMfaCode] = useState('');

    const handleLogin = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        setError('');
        try {
            const res = await fetch(`${API}/api/affiliates/login`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email, code })
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.message || 'Login failed');

            if (data.mfaRequired) {
                setMfaRequired(true);
                setChallengeToken(data.challengeToken);
                return;
            }

            localStorage.setItem('affiliateToken', data.token);
            onLogin(data.token);
        } catch (err: any) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleMfaVerify = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        setError('');
        try {
            const res = await fetch(`${API}/api/affiliates/login/verify`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ challengeToken, code: mfaCode })
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.message || 'Verification failed');

            localStorage.setItem('affiliateToken', data.token);
            onLogin(data.token);
        } catch (err: any) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    if (mfaRequired) {
        return (
            <div className="max-w-md mx-auto bg-white/95 backdrop-blur-2xl p-10 md:p-14 rounded-[2.5rem] shadow-2xl border border-white/40">
                <div className="w-16 h-16 bg-amber-100 text-amber-600 rounded-2xl flex items-center justify-center mb-8 mx-auto shadow-sm">
                    <i className="fas fa-key text-2xl"></i>
                </div>
                <h2 className="text-3xl font-black text-gray-900 mb-2 tracking-tighter text-center">
                    2FA Verification
                </h2>
                <p className="text-gray-500 mb-10 text-sm font-semibold text-center uppercase tracking-widest leading-relaxed">
                    Account protected by <br/><span className="text-primary font-black">Two-Factor Authentication</span>
                </p>

                {error && (
                    <div className="bg-zinc-50 border border-zinc-100 rounded-2xl p-4 text-zinc-600 text-xs font-bold flex items-center gap-3 mb-8">
                        <i className="fas fa-exclamation-circle text-zinc-400"></i> {error}
                    </div>
                )}

                <form onSubmit={handleMfaVerify} className="space-y-6">
                    <div className="space-y-2 text-center">
                        <label className="text-[10px] font-black uppercase tracking-widest text-gray-400">Authenticator Code</label>
                        <input
                            required
                            autoFocus
                            maxLength={6}
                            className="w-full text-center tracking-[1em] text-2xl bg-slate-50 border border-slate-100 rounded-2xl px-6 py-5 text-gray-900 font-bold outline-none focus:border-primary/30 transition-all font-mono"
                            value={mfaCode}
                            onChange={e => setMfaCode(e.target.value)}
                            placeholder="000000"
                        />
                    </div>
                    <button
                        type="submit"
                        disabled={loading}
                        className={`w-full py-5 rounded-2xl font-black text-white text-lg tracking-tight transition-all shadow-xl ${
                            loading
                            ? 'bg-slate-200 cursor-not-allowed grayscale'
                            : 'bg-emerald-600 hover:bg-emerald-700 hover:scale-[1.02] active:scale-[0.98] shadow-emerald-500/25'
                        }`}
                    >
                        {loading ? 'Verifying...' : 'Unlock Portal'}
                    </button>
                    <button type="button" onClick={() => setMfaRequired(false)} className="w-full text-xs font-bold text-gray-400 hover:text-gray-600 transition-colors">
                        Back to Login
                    </button>
                </form>
            </div>
        );
    }

    return (
        <div className="max-w-md mx-auto bg-white/95 backdrop-blur-2xl p-10 md:p-14 rounded-[2.5rem] shadow-2xl border border-white/40">
            <div className="w-16 h-16 bg-primary/10 text-primary rounded-2xl flex items-center justify-center mb-8 mx-auto shadow-sm">
                <i className="fas fa-shield-alt text-2xl"></i>
            </div>
            <h2 className="text-3xl font-black text-gray-900 mb-2 tracking-tighter text-center">
                Partner Login
            </h2>
            <p className="text-gray-500 mb-10 text-sm font-semibold text-center uppercase tracking-widest">
                Secure Access
            </p>

            {error && (
                <div className="bg-zinc-50 border border-zinc-100 rounded-2xl p-4 text-zinc-600 text-xs font-bold flex items-center gap-3 mb-8">
                    <i className="fas fa-exclamation-circle text-zinc-400"></i> {error}
                </div>
            )}

            <form onSubmit={handleLogin} className="space-y-6">
                <div className="space-y-2">
                    <label className="text-[10px] font-black uppercase tracking-widest text-gray-400 ml-1">Email Address</label>
                    <input 
                        required 
                        type="email" 
                        className="w-full bg-slate-50 border border-slate-100 rounded-2xl px-6 py-4 text-gray-900 font-semibold outline-none focus:border-primary/30 transition-all" 
                        value={email} 
                        onChange={e => setEmail(e.target.value)} 
                        placeholder="john@example.com" 
                    />
                </div>
                <div className="space-y-2">
                    <label className="text-[10px] font-black uppercase tracking-widest text-gray-400 ml-1">Affiliate Code</label>
                    <input 
                        required 
                        className="w-full bg-slate-50 border border-slate-100 rounded-2xl px-6 py-4 text-gray-900 font-semibold outline-none focus:border-primary/30 transition-all font-mono text-sm" 
                        value={code} 
                        onChange={e => setCode(e.target.value)} 
                        placeholder="LP-XXXXXX-XXXXXX" 
                    />
                </div>
                <button 
                    type="submit" 
                    disabled={loading} 
                    className={`w-full py-5 rounded-2xl font-black text-white text-lg tracking-tight transition-all shadow-xl ${
                        loading 
                        ? 'bg-slate-200 cursor-not-allowed grayscale' 
                        : 'bg-gradient-to-r from-primary to-secondary hover:scale-[1.02] active:scale-[0.98] shadow-primary/25'
                    }`}
                >
                    {loading ? 'Verifying...' : 'Access Portal'}
                </button>
            </form>
        </div>
    );
}

// ============================================
// MAIN AFFILIATE DASHBOARD
// ============================================
export default function AffiliateDashboard() {
    const [view, setView] = useState<'landing' | 'register' | 'login' | 'dashboard'>('landing');
    const [token, setToken] = useState<string | null>(null);
    const [activeTab, setActiveTab] = useState<'overview' | 'link' | 'referrals' | 'commissions' | 'resources' | 'security'>('overview');
    const [loading, setLoading] = useState(false);
    const [metrics, setMetrics] = useState<AffiliateMetrics | null>(null);
    const [affiliate, setAffiliate] = useState<AffiliateInfo | null>(null);
    const [referrals, setReferrals] = useState<Referral[]>([]);
    const [resources, setResources] = useState<MarketingResource[]>([]);
    const [referralLink, setReferralLink] = useState('');
    const [pixelUrl, setPixelUrl] = useState('');
    const [copied, setCopied] = useState<string | null>(null);
    const [resourceCategory, setResourceCategory] = useState('All');
    const [commissionData, setCommissionData] = useState<any>(null);
    const [error, setError] = useState('');
    const [mfaSetupData, setMfaSetupData] = useState<any>(null);
    const [mfaSetupCode, setMfaSetupCode] = useState('');

    // Check for existing token on mount
    useEffect(() => {
        const saved = localStorage.getItem('affiliateToken');
        if (saved) {
            setToken(saved);
            setView('dashboard');
        }
    }, []);

    // Load dashboard data when in dashboard view
    useEffect(() => {
        if (view === 'dashboard' && token) {
            fetchDashboard(token);
        }
    }, [view, token]);

    const fetchDashboard = async (authToken: string) => {
        setLoading(true);
        setError('');
        try {
            const headers = { Authorization: `Bearer ${authToken}` };

            const [dashRes, refRes, commRes, resRes] = await Promise.all([
                fetch(`${API}/api/affiliates/dashboard`, { headers }),
                fetch(`${API}/api/affiliates/referrals`, { headers }),
                fetch(`${API}/api/affiliates/commissions`, { headers }),
                fetch(`${API}/api/affiliates/resources`, { headers })
            ]);

            if (!dashRes.ok) {
                // Token might be expired
                localStorage.removeItem('affiliateToken');
                setToken(null);
                setView('login');
                throw new Error('Session expired. Please login again.');
            }

            const dashData = await dashRes.json();
            const refData = await refRes.json();
            const commData = await commRes.json();
            const resData = await resRes.json();

            setAffiliate(dashData.data.affiliate);
            setMetrics(dashData.data.metrics);
            setReferralLink(dashData.data.referralLink);
            setPixelUrl(dashData.data.pixelUrl);
            setReferrals(refData.data || []);
            setCommissionData(commData.data || null);
            setResources(resData.data || []);
        } catch (err: any) {
            setError(err.message || 'Failed to load dashboard');
        } finally {
            setLoading(false);
        }
    };

    const copyToClipboard = (text: string, label: string) => {
        navigator.clipboard.writeText(text);
        setCopied(label);
        setTimeout(() => setCopied(null), 2000);
    };

    const logout = () => {
        localStorage.removeItem('affiliateToken');
        setToken(null);
        setView('landing');
    };

    const statusColor = (status: string) => {
        const map: Record<string, { bg: string; text: string }> = {
            trial: { bg: '#ecfdf5', text: '#065f46' },
            pending_payout: { bg: '#fafaf9', text: '#44403c' },
            paid: { bg: '#10b981', text: '#ffffff' },
            approved: { bg: '#ecfdf5', text: '#065f46' },
            pending: { bg: '#fef9c3', text: '#854d0e' },
            suspended: { bg: '#18181b', text: '#ffffff' }
        };
        return map[status] || { bg: '#f1f5f9', text: '#475569' };
    };

    // ============================================
    // LANDING PAGE
    // ============================================
    if (view === 'landing') {
        return (
            <div className="bg-white font-sans text-gray-900 min-h-screen flex flex-col relative overflow-hidden">
                {/* DYNAMIC BACKGROUND IMAGE */}
                <div
                    className="absolute inset-0 z-0 opacity-[0.22] pointer-events-none"
                    style={{
                        backgroundImage: `url("/manchester_city_council/va_investors_dashboard.png")`,
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                        filter: 'grayscale(100%) brightness(0.9)'
                    }}
                />

                {/* NAV */}
                <nav className="bg-white/80 backdrop-blur-md shadow-sm sticky top-0 z-50 border-b border-gray-100">
                    <div className="max-w-7xl mx-auto px-6 flex justify-between items-center h-16">
                        <div className="flex items-center gap-2">
                            <div className="w-8 h-8 flex items-center justify-center rounded bg-primary shadow-sm">
                                <i className="fas fa-handshake text-white text-sm"></i>
                            </div>
                            <span className="font-bold text-lg text-gray-900 tracking-tight">Landlord Pro <span className="text-gray-400 font-normal mx-1">|</span> <span className="text-gray-600 font-semibold">Affiliates</span></span>
                        </div>
                        <div className="flex items-center gap-4">
                            <button onClick={() => setView('login')} className="text-gray-600 hover:text-primary font-semibold transition-colors">
                                Partner Login
                            </button>
                            <button onClick={() => setView('register')} className="bg-primary text-white px-5 py-2 rounded-lg font-bold hover:bg-secondary transition-all hover:scale-105 shadow-sm">
                                Join Network
                            </button>
                        </div>
                    </div>
                </nav>

                {/* HERO */}
                <section className="relative z-10 py-24 md:py-36 flex-1 flex flex-col justify-center">
                    <div className="max-w-5xl mx-auto px-6 text-center">
                        <div className="inline-block bg-primary/10 text-primary px-4 py-1.5 rounded-full text-sm font-bold mb-8 backdrop-blur-sm border border-primary/20">
                            High-Yield Partnership
                        </div>
                        <h1 className="text-5xl md:text-8xl font-black mb-8 text-gray-900 leading-[0.95] tracking-tighter">
                            Earn <span className="text-primary bg-clip-text text-transparent bg-gradient-to-r from-primary to-secondary">20% Recurring</span><br />for Life.
                        </h1>
                        <p className="text-xl md:text-2xl text-gray-600 mb-12 leading-relaxed max-w-3xl mx-auto font-medium">
                            The UK's largest property compliance ecosystem is looking for elite partners. Refer landlords and earn perpetual commission on every active subscription—delivered via automated payouts.
                        </p>
                        <div className="flex flex-col sm:flex-row gap-4 justify-center">
                            <button onClick={() => setView('register')} className="bg-primary text-white px-10 py-5 rounded-2xl font-bold hover:bg-secondary transition-all hover:scale-105 shadow-2xl shadow-primary/20 text-lg flex items-center justify-center gap-3">
                                Register as Partner <i className="fas fa-rocket"></i>
                            </button>
                            <button onClick={() => setView('login')} className="bg-white/80 backdrop-blur-sm border border-gray-200 text-gray-700 px-10 py-5 rounded-2xl font-bold hover:bg-gray-50 transition text-lg">
                                Affiliate Dashboard
                            </button>
                        </div>
                    </div>
                </section>

                {/* Performance Bar */}
                <section className="relative z-10 py-16 border-t border-gray-100 bg-white/40 backdrop-blur-sm">
                    <div className="max-w-7xl mx-auto px-6">
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-12">
                            <div className="flex flex-col items-center">
                                <div className="w-12 h-12 rounded-2xl bg-primary/10 text-primary flex items-center justify-center mb-4 shadow-sm">
                                    <i className="fas fa-percent text-xl"></i>
                                </div>
                                <h3 className="font-black text-gray-900 mb-1 tracking-tight">20% Recurring</h3>
                                <p className="text-[11px] font-bold text-gray-400 uppercase tracking-widest">Lifetime Commission</p>
                            </div>
                            <div className="flex flex-col items-center border-l border-gray-100 md:border-l-0 md:px-6">
                                <div className="w-12 h-12 rounded-2xl bg-secondary/10 text-secondary flex items-center justify-center mb-4 shadow-sm">
                                    <i className="fas fa-cookie text-xl"></i>
                                </div>
                                <h3 className="font-black text-gray-900 mb-1 tracking-tight">90-Day Cookie</h3>
                                <p className="text-[11px] font-bold text-gray-400 uppercase tracking-widest">Extended Tracking</p>
                            </div>
                            <div className="flex flex-col items-center border-t border-gray-100 pt-12 md:border-t-0 md:pt-0 md:border-x md:border-gray-100">
                                <div className="w-12 h-12 rounded-2xl bg-zinc-900 text-white flex items-center justify-center mb-4 shadow-sm">
                                    <i className="fas fa-chart-line text-xl"></i>
                                </div>
                                <h3 className="font-black text-gray-900 mb-1 tracking-tight">Live Analytics</h3>
                                <p className="text-[11px] font-bold text-gray-400 uppercase tracking-widest">Real-time Dashboard</p>
                            </div>
                            <div className="flex flex-col items-center border-t border-gray-100 pt-12 md:border-t-0 md:pt-0">
                                <div className="w-12 h-12 rounded-2xl bg-success/10 text-success flex items-center justify-center mb-4 shadow-sm">
                                    <i className="fas fa-bolt text-xl"></i>
                                </div>
                                <h3 className="font-black text-gray-900 mb-1 tracking-tight">Rapid Payouts</h3>
                                <p className="text-[11px] font-bold text-gray-400 uppercase tracking-widest">Automated System</p>
                            </div>
                        </div>
                    </div>
                </section>
            </div>
        );
    }

    // ============================================
    // REGISTRATION PAGE
    // ============================================
    if (view === 'register') {
        return (
            <div className="min-h-screen bg-slate-50 relative flex flex-col justify-center py-12 px-6">
                <div
                    className="absolute inset-0 z-0 opacity-[0.15] pointer-events-none"
                    style={{
                        backgroundImage: `url("/manchester_city_council/va_investors_dashboard.png")`,
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                        filter: 'grayscale(100%)'
                    }}
                />
                <div className="relative z-10 w-full max-w-4xl mx-auto">
                    <div className="flex justify-between items-center mb-10 px-4">
                        <button onClick={() => setView('landing')} className="text-gray-500 hover:text-primary font-bold transition-colors flex items-center gap-2">
                             <i className="fas fa-chevron-left"></i> Home
                        </button>
                        <button onClick={() => setView('login')} className="bg-white px-6 py-2.5 rounded-full text-primary font-bold shadow-sm border border-primary/10 hover:bg-gray-50 transition-all">
                            Login instead
                        </button>
                    </div>
                    <RegistrationForm onRegistered={(code, email) => {
                        setTimeout(() => setView('login'), 3000);
                    }} />
                </div>
            </div>
        );
    }

    // ============================================
    // LOGIN PAGE
    // ============================================
    if (view === 'login') {
        return (
            <div className="min-h-screen bg-slate-50 relative flex flex-col justify-center py-12 px-6">
                <div
                    className="absolute inset-0 z-0 opacity-[0.15] pointer-events-none"
                    style={{
                        backgroundImage: `url("/manchester_city_council/va_investors_dashboard.png")`,
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                        filter: 'grayscale(100%)'
                    }}
                />
                <div className="relative z-10 w-full max-w-lg mx-auto">
                     <div className="flex justify-between items-center mb-10 px-4">
                        <button onClick={() => setView('landing')} className="text-gray-500 hover:text-primary font-bold transition-colors flex items-center gap-2">
                             <i className="fas fa-chevron-left"></i> Home
                        </button>
                        <button onClick={() => setView('register')} className="bg-white px-6 py-2.5 rounded-full text-primary font-bold shadow-sm border border-primary/10 hover:bg-gray-50 transition-all">
                            New? Register
                        </button>
                    </div>
                    <AffiliateLogin onLogin={(t) => {
                        setToken(t);
                        setView('dashboard');
                    }} />
                </div>
            </div>
        );
    }

    // ============================================
    // DASHBOARD (authenticated via affiliate token)
    // ============================================
    if (loading && !affiliate) {
        return (
            <div style={{ minHeight: '100vh', background: '#f8fafc', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                <div style={{ width: 40, height: 40, border: '4px solid #e2e8f0', borderTopColor: '#6366f1', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
                <p style={{ color: '#64748b', marginTop: 16 }}>Loading Affiliate Dashboard...</p>
                <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
            </div>
        );
    }

    return (
        <div style={{ minHeight: '100vh', background: '#f8fafc' }}>
            {/* Top Bar */}
            <div className="bg-white border-b border-gray-100 px-6 h-16 flex justify-between items-center sticky top-0 z-50">
                <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded bg-primary flex items-center justify-center shadow-sm">
                        <i className="fas fa-handshake text-white text-sm"></i>
                    </div>
                    <span className="font-bold text-gray-900 tracking-tight">Landlord Pro <span className="text-gray-400 font-normal mx-1">|</span> <span className="text-gray-600">Partner Portal</span></span>
                </div>
                <div className="flex items-center gap-6">
                    <div className="hidden md:flex items-center gap-2">
                        <span className="text-sm font-semibold text-gray-700">{affiliate?.name}</span>
                        <span className="px-3 py-1 rounded-full text-[10px] font-black tracking-widest uppercase bg-primary/10 text-primary border border-primary/20">
                            {affiliate?.status}
                        </span>
                    </div>
                    <button onClick={logout} className="text-sm font-bold text-zinc-400 hover:text-zinc-900 transition-colors">
                        Logout
                    </button>
                </div>
            </div>

            {error && (
                <div className="mx-6 mt-4 bg-zinc-50 border border-zinc-100 rounded-2xl p-4 text-zinc-600 text-sm font-bold flex items-center gap-3">
                    <i className="fas fa-exclamation-circle text-zinc-400"></i> {error}
                </div>
            )}

            <div className="max-w-6xl mx-auto px-6 py-10">
                {/* Header Row */}
                <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-6 mb-10">
                    <div>
                        <h1 className="text-4xl font-black text-gray-900 leading-none tracking-tighter mb-2">Partner Hub</h1>
                        <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
                             <div className="flex items-center gap-2">
                                <span className="text-[10px] font-black uppercase tracking-widest text-gray-400">Partner Code</span>
                                <code className="bg-primary/5 text-primary text-xs px-3 py-1 rounded-full font-mono font-black border border-primary/10">{affiliate?.code}</code>
                             </div>
                             <div className="flex items-center gap-2">
                                <span className="text-[10px] font-black uppercase tracking-widest text-gray-400">Revenue Share</span>
                                <span className="text-secondary text-xs font-black uppercase tracking-widest">{affiliate?.commissionRate}% Per Client</span>
                             </div>
                             {affiliate?.agreementAcceptedAt && (
                                <div className="flex items-center gap-2">
                                    <span className="text-[10px] font-black uppercase tracking-widest text-gray-400">Agreement</span>
                                    <span className="text-emerald-600 text-xs font-black uppercase tracking-widest flex items-center gap-1">
                                        <i className="fas fa-check-circle"></i> Signed {new Date(affiliate.agreementAcceptedAt).toLocaleDateString()}
                                    </span>
                                </div>
                             )}
                        </div>
                    </div>
                </div>

                {/* Tabs */}
                <div className="flex gap-2 bg-gray-100/50 p-1.5 rounded-2xl mb-12 overflow-x-auto no-scrollbar">
                    {[
                        { key: 'overview', label: '📊 Overview' },
                        { key: 'link', label: '🔗 My Link' },
                        { key: 'referrals', label: '👥 Referrals' },
                        { key: 'commissions', label: '💰 Commissions' },
                        { key: 'resources', label: '📦 Resources' },
                        { key: 'security', label: '🛡️ Security' }
                    ].map(tab => (
                        <button 
                            key={tab.key} 
                            onClick={() => setActiveTab(tab.key as any)} 
                            className={`flex-1 min-w-[120px] py-3 px-4 rounded-xl text-sm font-black transition-all duration-300 ${
                                activeTab === tab.key 
                                ? 'bg-gradient-to-r from-primary to-secondary text-white shadow-lg shadow-primary/25' 
                                : 'text-gray-400 hover:text-gray-600 hover:bg-white/50'
                            }`}
                        >
                            {tab.label}
                        </button>
                    ))}
                </div>

                {/* OVERVIEW TAB */}
                {activeTab === 'overview' && metrics && (
                    <div className="space-y-8">
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                            {[
                                { label: 'Total Clicks', value: metrics.totalClicks, color: 'text-gray-900', bg: 'bg-white', icon: 'fa-mouse-pointer' },
                                { label: 'Trial Signups', value: metrics.totalTrials, color: 'text-primary', bg: 'bg-white', icon: 'fa-user-clock' },
                                { label: 'Conversions', value: metrics.totalConversions, color: 'text-success', bg: 'bg-white', icon: 'fa-check-double' },
                                { label: 'Conversion Rate', value: metrics.conversionRate, color: 'text-secondary', bg: 'bg-white', icon: 'fa-chart-pie' }
                            ].map((card, i) => (
                                <div key={i} className={`${card.bg} rounded-[2rem] border border-gray-100 p-8 shadow-sm hover:shadow-xl transition-all group`}>
                                    <div className="w-10 h-10 rounded-xl bg-gray-50 flex items-center justify-center text-gray-400 mb-6 group-hover:bg-primary/5 group-hover:text-primary transition-colors">
                                        <i className={`fas ${card.icon}`}></i>
                                    </div>
                                    <div className={`text-4xl font-black ${card.color} mb-1 tracking-tighter`}>{card.value}</div>
                                    <div className="text-[10px] font-black text-gray-400 uppercase tracking-widest">{card.label}</div>
                                </div>
                            ))}
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                            <div className="bg-gradient-to-br from-primary to-secondary rounded-[2rem] p-10 text-white shadow-2xl shadow-primary/20 relative overflow-hidden group">
                                <div className="absolute top-0 right-0 p-8 opacity-10 group-hover:scale-110 transition-transform">
                                    <i className="fas fa-coins text-8xl"></i>
                                </div>
                                <div className="text-[10px] font-black opacity-60 uppercase tracking-widest mb-4">Cumulative Revenue</div>
                                <div className="text-5xl font-black tracking-tighter mb-2">£{(metrics.totalEarnings || 0).toFixed(2)}</div>
                                <p className="text-xs font-medium opacity-60">Verified earnings for life.</p>
                            </div>
                            <div className="bg-white border-2 border-primary/20 rounded-[2rem] p-10 text-primary shadow-xl relative overflow-hidden group">
                                <div className="absolute top-0 right-0 p-8 opacity-5 group-hover:scale-110 transition-transform">
                                    <i className="fas fa-clock text-8xl"></i>
                                </div>
                                <div className="text-[10px] font-black text-gray-300 uppercase tracking-widest mb-4">Pending Clearing</div>
                                <div className="text-5xl font-black tracking-tighter text-gray-900 mb-2">£{(metrics.pendingPayout || 0).toFixed(2)}</div>
                                <p className="text-xs font-bold text-gray-400 uppercase tracking-widest">Scheduled for next cycle</p>
                            </div>
                            <div className="bg-slate-900 rounded-[2rem] p-10 text-white shadow-2xl relative overflow-hidden group">
                                <div className="absolute top-0 right-0 p-8 opacity-20 group-hover:scale-110 transition-transform">
                                    <i className="fas fa-check-circle text-8xl text-success"></i>
                                </div>
                                <div className="text-[10px] font-black text-gray-500 uppercase tracking-widest mb-4">Total Realised</div>
                                <div className="text-5xl font-black tracking-tighter mb-2">£{(metrics.paidOut || 0).toFixed(2)}</div>
                                <p className="text-xs font-bold text-success uppercase tracking-widest">Payouts complete</p>
                            </div>
                        </div>
                    </div>
                )}

                {/* MY LINK TAB (US-034) */}
                {activeTab === 'link' && (
                    <div className="bg-white rounded-3xl border border-gray-100 shadow-sm p-8 md:p-12">
                        <h2 className="text-2xl font-black text-gray-900 mb-8 flex items-center gap-3">
                            <span className="w-10 h-10 rounded-xl bg-primary/10 text-primary flex items-center justify-center">
                                <i className="fas fa-link"></i>
                            </span>
                            Your Referral Assets
                        </h2>

                        {/* Main Referral Link */}
                        <div className="mb-10">
                            <label className="text-[10px] font-black uppercase tracking-widest text-gray-400 mb-3 block">Personal Referral Link (30-day cookie tracking)</label>
                            <div className="flex flex-col sm:flex-row gap-2">
                                <input 
                                    type="text" 
                                    readOnly 
                                    value={referralLink} 
                                    className="flex-1 bg-gray-50 border border-gray-100 rounded-2xl px-6 py-4 font-mono text-sm text-primary font-bold outline-none focus:border-primary/30 transition-colors"
                                />
                                <button 
                                    onClick={() => copyToClipboard(referralLink, 'link')} 
                                    className="bg-primary hover:bg-secondary text-white font-black px-10 py-4 rounded-2xl shadow-lg shadow-primary/20 transition-all active:scale-95 whitespace-nowrap"
                                >
                                    {copied === 'link' ? '✓ Copied!' : 'Copy Link'}
                                </button>
                            </div>
                        </div>

                        {/* Spy Pixel */}
                        <div className="mb-12">
                            <label className="text-[10px] font-black uppercase tracking-widest text-gray-400 mb-3 block">Stealth Tracking Pixel (for emails & web pages)</label>
                            <div className="flex flex-col sm:flex-row gap-2">
                                <input 
                                    type="text" 
                                    readOnly 
                                    value={`<img src="${pixelUrl}" width="1" height="1" />`} 
                                    className="flex-1 bg-gray-50 border border-gray-100 rounded-2xl px-6 py-4 font-mono text-xs text-gray-600 outline-none focus:border-primary/30 transition-colors"
                                />
                                <button 
                                    onClick={() => copyToClipboard(`<img src="${pixelUrl}" width="1" height="1" />`, 'pixel')} 
                                    className="bg-slate-900 hover:bg-black text-white font-black px-8 py-4 rounded-2xl shadow-xl transition-all active:scale-95 whitespace-nowrap text-xs uppercase tracking-widest"
                                >
                                    {copied === 'pixel' ? '✓ Copied!' : 'Copy Code'}
                                </button>
                            </div>
                        </div>

                        {/* How It Works */}
                        <div className="bg-primary/[0.03] border border-primary/10 rounded-[2rem] p-8 md:p-10 relative overflow-hidden group">
                             <div className="absolute -top-10 -right-10 w-40 h-40 bg-primary/5 rounded-full blur-3xl group-hover:bg-primary/10 transition-colors duration-500"></div>
                             <h3 className="font-black text-gray-900 mb-6 text-xl tracking-tight leading-none">The Payout Mechanics</h3>
                             <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-4">
                                {[
                                    'Share link via email, socials, or direct message',
                                    '30-day attribution window per unique click',
                                    'Unlimited attribution for multi-device journeys',
                                    `Commission (${affiliate?.commissionRate}%) active from 2nd paid month`,
                                    'Automated verification & bank wire payouts'
                                ].map((text, i) => (
                                    <div key={i} className="flex items-start gap-3">
                                        <div className="w-5 h-5 rounded-full bg-primary/10 text-primary flex items-center justify-center mt-0.5 shrink-0">
                                            <i className="fas fa-check text-[10px]"></i>
                                        </div>
                                        <span className="text-gray-600 text-sm font-medium leading-relaxed">{text}</span>
                                    </div>
                                ))}
                             </div>
                        </div>
                    </div>
                )}

                {/* REFERRALS TAB */}
                {activeTab === 'referrals' && (
                    <div className="bg-white rounded-3xl border border-gray-100 shadow-sm overflow-hidden">
                        <div className="px-8 py-6 border-b border-gray-50 flex justify-between items-center">
                            <h2 className="text-xl font-black text-gray-900 leading-none">Referral Network</h2>
                            <span className="bg-primary/5 text-primary text-[10px] font-black uppercase tracking-widest px-3 py-1 rounded-full">
                                {referrals.length} Total Partners
                            </span>
                        </div>
                        {referrals.length === 0 ? (
                            <div className="py-24 text-center">
                                <i className="fas fa-users text-gray-100 text-6xl mb-6"></i>
                                <p className="text-gray-400 font-medium tracking-tight">Your network is empty. Start sharing your link!</p>
                            </div>
                        ) : (
                            <div className="overflow-x-auto">
                                <table className="w-full text-left">
                                    <thead>
                                        <tr className="bg-gray-50/50">
                                            {['User Email', 'Status', 'Earnings', 'Month', 'Date'].map(h => (
                                                <th key={h} className="px-8 py-4 text-[10px] font-black uppercase tracking-widest text-gray-400 border-b border-gray-50">{h}</th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-50">
                                        {referrals.map(ref => {
                                            const sc = statusColor(ref.status);
                                            return (
                                                <tr key={ref.id} className="hover:bg-gray-50/30 transition-colors">
                                                    <td className="px-8 py-5 text-sm font-semibold text-gray-900">{ref.referredEmail}</td>
                                                    <td className="px-8 py-5">
                                                        <span className="px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest" style={{ background: sc.bg, color: sc.text }}>
                                                            {ref.status.replace('_', ' ')}
                                                        </span>
                                                    </td>
                                                    <td className="px-8 py-5 text-sm font-black text-primary">
                                                        {ref.commissionAmount > 0 ? `£${ref.commissionAmount.toFixed(2)}` : '—'}
                                                    </td>
                                                    <td className="px-8 py-5 text-sm font-medium text-gray-400">Month {ref.paymentMonth}</td>
                                                    <td className="px-8 py-5 text-sm font-medium text-gray-400 italic">{new Date(ref.createdAt).toLocaleDateString()}</td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                )}

                {/* COMMISSIONS TAB (US-035) */}
                {activeTab === 'commissions' && commissionData && (
                    <div className="space-y-8">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                            <div className="bg-white rounded-3xl border border-blue-50 p-8 shadow-sm">
                                <div className="text-[10px] font-black uppercase tracking-widest text-blue-400 mb-4">Trial Stage</div>
                                <div className="text-4xl font-black text-gray-900 mb-1">{commissionData.commissions?.trial?.length || 0}</div>
                                <div className="text-sm font-medium text-gray-400">Awaiting conversion</div>
                            </div>
                            <div className="bg-white rounded-3xl border border-amber-50 p-8 shadow-sm">
                                <div className="text-[10px] font-black uppercase tracking-widest text-amber-500 mb-4">Vesting / Pending</div>
                                <div className="text-4xl font-black text-gray-900 mb-1">£{(commissionData.summary?.totalPendingPayout || 0).toFixed(2)}</div>
                                <div className="text-sm font-medium text-gray-400">From {commissionData.commissions?.pendingPayout?.length || 0} active users</div>
                            </div>
                            <div className="bg-white rounded-3xl border border-success/10 p-8 shadow-sm">
                                <div className="text-[10px] font-black uppercase tracking-widest text-success mb-4">Verified Payout</div>
                                <div className="text-4xl font-black text-gray-900 mb-1">£{(commissionData.summary?.totalPaid || 0).toFixed(2)}</div>
                                <div className="text-sm font-medium text-gray-400">Total lifetime commissions</div>
                            </div>
                        </div>
                        <div className="bg-primary/5 border border-primary/10 rounded-2xl p-6 flex items-start gap-4">
                            <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center shadow-sm text-primary shrink-0">
                                <i className="fas fa-info-circle"></i>
                            </div>
                            <div>
                                <h4 className="font-bold text-gray-900 mb-1 leading-none">Payout Policy</h4>
                                <p className="text-sm text-gray-600 leading-relaxed">{commissionData.summary?.payoutTrigger}</p>
                            </div>
                        </div>
                    </div>
                )}

                {/* RESOURCES TAB (T-007) */}
                {activeTab === 'resources' && (
                    <div className="space-y-6">
                        <div className="flex gap-2 pb-4 overflow-x-auto no-scrollbar">
                            {['All', 'Banners', 'Email Templates', 'Social Media', 'Competitor Comparisons'].map(cat => (
                                <button key={cat} onClick={() => setResourceCategory(cat)} className={`px-5 py-2 rounded-full text-xs font-black uppercase tracking-widest transition-all ${resourceCategory === cat ? 'bg-primary text-white shadow-lg shadow-primary/20' : 'bg-white text-gray-400 border border-gray-100 hover:border-primary/30 hover:text-primary'}`}>
                                    {cat}
                                </button>
                            ))}
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            {resources
                                .filter(r => resourceCategory === 'All' || r.category === resourceCategory)
                                .map(resource => (
                                    <div key={resource.id} className="bg-white rounded-3xl border border-gray-100 p-8 shadow-sm hover:shadow-xl transition-all group">
                                        <div className="flex justify-between items-start mb-6">
                                            <div>
                                                <span className="text-[10px] font-black text-primary uppercase tracking-[0.2em]">{resource.category}</span>
                                                <h3 className="text-xl font-black text-gray-900 mt-1 tracking-tight">{resource.title}</h3>
                                            </div>
                                            <span className="bg-gray-50 text-gray-400 text-[10px] font-black uppercase tracking-widest px-3 py-1 rounded-full border border-gray-100">{resource.format}</span>
                                        </div>
                                        <p className="text-gray-500 text-sm leading-relaxed mb-8">{resource.description}</p>

                                        {resource.content && (
                                            <details className="mb-8 group/details">
                                                <summary className="text-xs font-black text-primary cursor-pointer uppercase tracking-widest list-none flex items-center gap-2">
                                                    <i className="fas fa-eye"></i> Preview Asset
                                                </summary>
                                                <div className="mt-4 p-6 bg-slate-50 rounded-2xl border border-slate-100">
                                                    <pre className="text-[11px] text-slate-600 font-mono whitespace-pre-wrap leading-relaxed max-h-48 overflow-auto">
                                                        {resource.content}
                                                    </pre>
                                                </div>
                                            </details>
                                        )}

                                        <div className="flex gap-3">
                                            {resource.content && (
                                                <button onClick={() => copyToClipboard(resource.content || '', resource.id)} className="bg-primary text-white text-xs font-black uppercase tracking-widest py-3 px-6 rounded-xl shadow-lg shadow-primary/20 hover:scale-105 transition-transform active:scale-95">
                                                    {copied === resource.id ? '✓ Copied' : '📋 Copy Content'}
                                                </button>
                                            )}
                                            {resource.downloadUrl && (
                                                <button className="bg-slate-100 text-slate-500 text-xs font-black uppercase tracking-widest py-3 px-6 rounded-xl hover:bg-slate-200 transition-colors">
                                                    <i className="fas fa-download mr-1"></i> Asset
                                                </button>
                                            )}
                                        </div>

                                        <div className="mt-8 flex gap-2 flex-wrap">
                                            {resource.tags.map(tag => (
                                                <span key={tag} className="text-[9px] font-bold text-gray-300 uppercase tracking-widest">#{tag}</span>
                                            ))}
                                        </div>
                                    </div>
                                ))}
                        </div>
                    </div>
                )}

                {/* SECURITY TAB (2FA) */}
                {activeTab === 'security' && (
                    <div className="max-w-2xl mx-auto space-y-8 animate-fadeIn">
                        <div className="bg-white rounded-[2.5rem] border border-gray-100 shadow-sm p-10 md:p-14">
                            <div className="flex items-center gap-6 mb-10">
                                <div className={`w-16 h-16 rounded-2xl flex items-center justify-center text-2xl ${affiliate?.twoFactorEnabled ? 'bg-success/10 text-success' : 'bg-gray-100 text-gray-400'}`}>
                                    <i className={`fas ${affiliate?.twoFactorEnabled ? 'fa-shield-check' : 'fa-shield-alt'}`}></i>
                                </div>
                                <div>
                                    <h2 className="text-2xl font-black text-gray-900 tracking-tight">Two-Factor Authentication</h2>
                                    <p className="text-gray-500 font-medium">Add an extra layer of security to your partner account.</p>
                                </div>
                            </div>

                            {affiliate?.twoFactorEnabled ? (
                                <div className="space-y-8">
                                    <div className="bg-success/5 border border-success/10 rounded-2xl p-6 flex items-start gap-4">
                                        <div className="w-8 h-8 rounded-full bg-success text-white flex items-center justify-center shrink-0">
                                            <i className="fas fa-check text-xs"></i>
                                        </div>
                                        <div>
                                            <h4 className="font-bold text-gray-900 mb-1 leading-none">Status: Active & Protected</h4>
                                            <p className="text-sm text-gray-600 leading-relaxed">Your account currently requires a dynamic code from your authenticator app to log in.</p>
                                        </div>
                                    </div>
                                    <button 
                                        onClick={async () => {
                                            if (!window.confirm('Are you sure you want to disable 2FA? This will make your account less secure.')) return;
                                            const res = await fetch(`${API}/api/affiliates/2fa/disable`, {
                                                method: 'POST',
                                                headers: { Authorization: `Bearer ${token}` }
                                            });
                                            if (res.ok) fetchDashboard(token || '');
                                        }}
                                        className="w-full py-5 rounded-2xl border-2 border-zinc-100 text-zinc-400 font-black text-sm uppercase tracking-widest hover:bg-zinc-50 transition-all hover:text-zinc-800"
                                    >
                                        Disable Two-Factor Authentication
                                    </button>
                                </div>
                            ) : (
                                <div className="space-y-8">
                                    <p className="text-gray-600 leading-relaxed">
                                        Enhance your account security by requiring a six-digit verification code from an authenticator app (like Google Authenticator or Authy) whenever you log in.
                                    </p>
                                    
                                    <button 
                                        onClick={async () => {
                                            setLoading(true);
                                            try {
                                                const res = await fetch(`${API}/api/affiliates/2fa/setup`, {
                                                    method: 'POST',
                                                    headers: { Authorization: `Bearer ${token}` }
                                                });
                                                const data = await res.json();
                                                if (data.success) {
                                                    setMfaSetupData(data);
                                                } else {
                                                    alert(data.message || 'Failed to initiate 2FA setup');
                                                }
                                            } catch (err) {
                                                alert('Failed to connect to 2FA service');
                                            } finally {
                                                setLoading(false);
                                            }
                                        }}
                                        className="w-full py-5 bg-emerald-600 text-white font-black rounded-2xl shadow-xl shadow-success/20 hover:scale-[1.02] active:scale-[0.98] transition-all text-lg tracking-tight"
                                    >
                                        Initiate 2FA Setup
                                    </button>
                                </div>
                            )}

                            {mfaSetupData && !affiliate?.twoFactorEnabled && (
                                <div className="mt-10 pt-10 border-t border-gray-100 space-y-8 animate-fadeIn">
                                    <div className="flex flex-col items-center text-center">
                                        <div className="bg-white p-4 rounded-3xl shadow-xl border border-gray-100 mb-6 transition-transform hover:scale-105">
                                            <img src={mfaSetupData.qrCodeUrl} alt="2FA QR Code" className="w-48 h-48" />
                                        </div>
                                        <p className="text-sm text-gray-500 font-medium max-w-xs mx-auto">
                                            Scan this QR code with your authenticator app (Google Authenticator, Authy, etc.)
                                        </p>
                                    </div>

                                    <div className="space-y-4">
                                        <div className="p-4 bg-slate-50 rounded-2xl border border-slate-100 flex flex-col items-center">
                                            <span className="text-[10px] font-black uppercase tracking-widest text-gray-400 mb-1">Manual Entry Secret</span>
                                            <code className="text-sm font-mono font-bold text-primary">{mfaSetupData.secret}</code>
                                        </div>

                                        <div className="space-y-2">
                                            <label className="text-[10px] font-black uppercase tracking-widest text-gray-400 ml-1">Verification Code</label>
                                            <input 
                                                maxLength={6}
                                                className="w-full text-center tracking-[0.5em] bg-slate-50 border border-slate-100 rounded-2xl px-6 py-4 text-gray-900 font-bold outline-none focus:border-primary/30 transition-all font-mono"
                                                value={mfaSetupCode}
                                                onChange={e => setMfaSetupCode(e.target.value)}
                                                placeholder="000000"
                                            />
                                        </div>

                                        <div className="flex gap-4 pt-4">
                                            <button 
                                                onClick={() => setMfaSetupData(null)}
                                                className="flex-1 py-4 rounded-xl border-2 border-slate-100 text-slate-400 font-black text-xs uppercase tracking-widest hover:bg-slate-50 transition-all"
                                            >
                                                Cancel
                                            </button>
                                            <button 
                                                onClick={async () => {
                                                    setLoading(true);
                                                    try {
                                                        const res = await fetch(`${API}/api/affiliates/2fa/enable`, {
                                                            method: 'POST',
                                                            headers: { 
                                                                'Content-Type': 'application/json',
                                                                Authorization: `Bearer ${token}` 
                                                            },
                                                            body: JSON.stringify({ code: mfaSetupCode })
                                                        });
                                                        const data = await res.json();
                                                        if (data.success) {
                                                            setMfaSetupData(null);
                                                            setMfaSetupCode('');
                                                            alert('2FA enabled successfully!');
                                                            fetchDashboard(token || '');
                                                        } else {
                                                            alert(data.message || 'Invalid code');
                                                        }
                                                    } finally {
                                                        setLoading(false);
                                                    }
                                                }}
                                                className="flex-[2] py-4 bg-emerald-600 text-white font-black rounded-xl shadow-lg shadow-success/20 hover:scale-105 active:scale-95 transition-all text-xs uppercase tracking-widest"
                                            >
                                                Verify & Enable
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

